import { isAfter } from 'date-fns';

export interface EventSession {
  startsAt: string;
  endsAt: string;
}

interface GroupWithSlots {
  startsAt?: string | null;
  endsAt?: string | null;
  timeSlots?: EventSession[] | null;
}

export interface EventWithSchedule {
  startDateTime: string;
  endDateTime: string;
  groups?: GroupWithSlots[] | null;
}

export interface EventSchedule {
  /** Every session across all groups, earliest first. The event window when no group has slots. */
  sessions: EventSession[];
  /** The session to show on a card: the first one still to finish, or the last one once the program is over. */
  current: EventSession;
  /** 1-based position of `current` in `sessions`. */
  currentIndex: number;
  isMultiSession: boolean;
}

const bySlotStart = (a: EventSession, b: EventSession) =>
  new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();

/**
 * A multi-week program is one event whose group holds one time slot per week, so
 * `event.startDateTime` is only the first week. Cards and listings read the slot
 * schedule instead of the event window through this.
 */
export const getEventSchedule = (event: EventWithSchedule, now = new Date()): EventSchedule => {
  const slots = (event.groups ?? [])
    .flatMap((group) => group.timeSlots ?? [])
    .filter((slot) => slot.startsAt && slot.endsAt);

  const sessions = (
    slots.length > 0 ? slots : [{ startsAt: event.startDateTime, endsAt: event.endDateTime }]
  ).sort(bySlotStart);

  const upcomingIndex = sessions.findIndex((session) => isAfter(new Date(session.endsAt), now));
  const index = upcomingIndex === -1 ? sessions.length - 1 : upcomingIndex;

  // Slots all ended but the event has not: the slots are stale, so show the event window.
  const isSlotDataStale =
    upcomingIndex === -1 && slots.length > 0 && isAfter(new Date(event.endDateTime), now);

  return {
    sessions,
    current: isSlotDataStale
      ? { startsAt: event.startDateTime, endsAt: event.endDateTime }
      : sessions[index],
    currentIndex: index + 1,
    isMultiSession: sessions.length > 1,
  };
};

/** The current session as the `{ startDateTime, endDateTime }` pair card mappers spread in. */
export const getCurrentSessionWindow = (event: EventWithSchedule, now = new Date()) => {
  const { current } = getEventSchedule(event, now);
  return { startDateTime: current.startsAt, endDateTime: current.endsAt };
};

/** A group whose one price covers more than one dated session. */
export const isPackGroup = (group: { timeSlots?: { id: string }[] | null }) =>
  (group.timeSlots?.length ?? 0) > 1;

export const isPackEvent = (event: { groups?: { timeSlots?: { id: string }[] | null }[] | null }) =>
  (event.groups ?? []).some(isPackGroup);
